import type { CreatedEventResponse } from '../api/events';
import { getEventCoverImage } from '../api/events';
import type { LocationBody } from '../services/locationService';
import { wallClockFromUtcIso } from './eventDate';

/**
 * Temporary hand-off between event creation → event success page → gig creation.
 * Uses sessionStorage (tab-scoped, cleared when the tab closes) so stale data never
 * leaks into later sessions; every key is also removed once consumed.
 */

export interface EventSuccessSnapshot {
  eventId: string;
  eventUuid?: string;
  projectUuid?: string;
  title: string;
  start?: string;
  end?: string;
  timezone?: string;
  imageUrl?: string | null;
  info?: string;
  location?: LocationBody | null;
  isExternal?: boolean;
}

export interface GigPrefill {
  eventId: string;
  eventTitle: string;
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  timezone?: string;
  locationBody: LocationBody | null;
}

const successKey = (eventId: string) => `event_success:${eventId}`;
const GIG_PREFILL_KEY = 'gig_prefill_from_event';

function read<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable — callers fall back gracefully
  }
}

/** Builds + stores the snapshot from the create-event response. Returns the event id used as key. */
export function saveEventSuccess(
  data: CreatedEventResponse,
  location: LocationBody | null,
  /** Uploaded cover URL: the create response returns `medias: []` while media processes in the background. */
  uploadedImageUrl?: string | null,
): string | null {
  const id = data.event_id ?? data.event?.id;
  if (!id) return null;
  const eventId = String(id);
  const event = { ...data.event, medias: data.medias ?? data.event?.medias };
  const snapshot: EventSuccessSnapshot = {
    eventId,
    eventUuid: data.event_uuid ?? data.event?.event_uuid,
    projectUuid: data.project?.project_uuid,
    title: data.event?.event_title ?? '',
    start: data.event?.start,
    end: data.event?.end,
    timezone: data.event?.iana_timezone ?? data.event?.timezone,
    imageUrl: getEventCoverImage(event) ?? uploadedImageUrl ?? null,
    info: data.event?.info,
    location,
    isExternal:
      data.event?.event_ticketing_type === 'external' ||
      Boolean(data.event?.external_ticketing_url),
  };
  write(successKey(eventId), snapshot);
  return eventId;
}

export function getEventSuccess(eventId: string): EventSuccessSnapshot | null {
  return read<EventSuccessSnapshot>(successKey(eventId));
}

export function saveGigPrefill(snapshot: EventSuccessSnapshot) {
  const start = snapshot.start ? wallClockFromUtcIso(snapshot.start) : null;
  const end = snapshot.end ? wallClockFromUtcIso(snapshot.end) : null;
  const prefill: GigPrefill = {
    eventId: snapshot.eventId,
    eventTitle: snapshot.title,
    title: '',
    description: snapshot.title ? `For the event "${snapshot.title}".` : '',
    startDate: start?.date ?? '',
    startTime: start?.time ?? '',
    endDate: end?.date ?? '',
    endTime: end?.time ?? '',
    timezone: snapshot.timezone,
    locationBody: snapshot.location ?? null,
  };
  write(GIG_PREFILL_KEY, prefill);
}

/** Peeks the prefill for this event (safe under StrictMode double-render). */
export function getGigPrefill(eventId: string): GigPrefill | null {
  const prefill = read<GigPrefill>(GIG_PREFILL_KEY);
  return prefill && prefill.eventId === eventId ? prefill : null;
}

export function clearGigPrefill() {
  try {
    sessionStorage.removeItem(GIG_PREFILL_KEY);
  } catch {
    // ignore
  }
}
